<template>
    <editor-sidebar class="properties-select-block" floating :visible="visible" body-class="overflow-auto properties-select-block-body">
        <template #header>
            <b-btn variant="light" class="mr-2" @click="$emit('close')" :title="'Cancel|Annuler'|tr">
                <i class="fas fa-close"/>
            </b-btn>
            <h4 class="sidebar-title">{{ title |tr }}</h4>
        </template>
        <b-tabs class="tabs-pills" v-model="activeTab">
            <b-tab :title="'Library|Bibliothèque'|tr">
                <div v-for="(category, index) in categories" :key="`category-${index}`">
                    <h4 v-if="category.types.length">{{category.label|tr}}</h4>
                    <b-card no-body>
                        <b-list-group class="page-blocks">
                            <b-list-group-item v-for="(preset, index) in category.types" :key="'preset-' + index" @click="selectPreset(preset)" class="interactive">
                                <i class="fas fa-fw mr-2" :class="preset.typeDef.glyph"/>
                                <span>{{preset.typeDef.name|tr}}</span>
                            </b-list-group-item>
                        </b-list-group>
                    </b-card>
                </div>
            </b-tab>
            <b-tab :title="'My Blocks|Mes Blocs'|tr" class="pb-5 mb-5">
                <div v-if="templates.length === 0" class="text-center f-500 f-14 my-4">
                    <span v-tr>No saved templates yet.|Aucun modèle sauvegardé pour le moment.</span>
                </div>
                <div v-else>
                    <div class="template-grid">
                        <template-block-card
                            v-for="(template, index) in templates"
                            :key="'template-' + index"
                            class="template-item"
                            :template="template"
                            :preview-height="100"
                            orientation="horizontal"
                            @select="openTemplatePreview"
                        />
                    </div>
                </div>
            </b-tab>
        </b-tabs>

        <btn-checkbox v-if="childOnly && (!childClasses || childClasses.length === 0)" v-model="showAll" class="mt-3 w-100">
            <span v-tr>Show All|Tout afficher</span>
        </btn-checkbox>

        <div v-if="activeTab === 1" class="properties-select-block-footer">
            <b-btn variant="outline-primary" class="w-100" @click="openManageTemplates">
                <i class="fas fa-layer-group mr-2"/>
                <span v-tr>Manage Templates|Gérer les modèles</span>
            </b-btn>
        </div>

        <b-modal v-if="previewTemplateModalVisible" v-model="previewTemplateModalVisible" :title="previewTemplate ? previewTemplate.displayName : ''" size="lg" body-bg-variant="white">
            <div class="template-preview-modal-content" v-if="previewTemplate">
                <div class="template-large-preview mb-3">
                    <block-preview :block="previewTemplate.block" :scale="0.6" />
                </div>
                <div v-if="previewTemplate.description" class="template-description mb-3">
                    <b-form-group label="Description">
                        <div>{{previewTemplate.description|tr}}</div>
                    </b-form-group>
                </div>
            </div>
            <template #modal-footer>
                <b-btn variant="outline-danger" size="sm" class="px-3 py-2" @click="deleteTemplate(previewTemplate)">
                    <i class="fas fa-trash mr-2"/>
                    <span v-tr>Delete|Supprimer</span>
                </b-btn>
                <b-btn variant="outline-primary" size="sm" class="ml-2 px-3 py-2" @click="editTemplateBlock(previewTemplate)">
                    <i class="fas fa-pen-to-square mr-2"/>
                    <span v-tr>Edit Block|Modifier le bloc</span>
                </b-btn>
                <b-btn variant="primary" size="sm"  class="ml-auto px-3 py-2" @click="insertTemplate(previewTemplate)">
                    <i class="fas fa-plus mr-2"/>
                    <span v-tr>Insert Block|Insérer le bloc</span>
                </b-btn>
            </template>
        </b-modal>

    </editor-sidebar>
</template>

<script>
import BtnCheckbox from "@/components/editor/properties/inputs/btn-checkbox.vue";
import CmsBlockTemplate from "@/helpers/editor/templates/CmsBlockTemplate.js";

// properties-select-block #hot-reload-debug
import EditorSidebar from "@/layout/editor-sidebar.vue";
import TemplateBlockCard from "@/components/editor/templates/template-block-card.vue";
import BlockPreview from "@/components/editor/properties/components/block-preview.vue";

export default {
    name: `properties-select-block`,
    components: {BtnCheckbox, EditorSidebar, TemplateBlockCard, BlockPreview},
    props: {
        visible: {type: Boolean},
        title: {type: String, default: `Add Section|Ajouter section`},
        childClasses: {type: Array},
        childOnly: {type: Boolean},
        parentOnly: {type: Boolean},
        templates: {type: Array, default: () => []}
    },
    data() {
        return {
            showAll: false,
            previewTemplateModalVisible: false,
            previewTemplate: null,
            activeTab: 0
        }
    },
    computed: {
        typesFiltered() {
            if (this.parentOnly) {
                if (this.showAll) {
                    return window.cmsFactory.constructors.filter(c => !c.typeDef.hidden);
                } else {
                    return window.cmsFactory.constructors.filter(c => c.typeDef.isParent && !c.typeDef.hidden);
                }
            } else if (this.childOnly) {
                if (this.childClasses && this.childClasses.length > 0) {
                    return this.childClasses;
                } else {
                    if (this.showAll) {
                        return window.cmsFactory.constructors.filter(c => !c.typeDef.hidden);
                    } else {
                        return window.cmsFactory.constructors.filter(c => !c.typeDef.isParent && !c.typeDef.hidden);
                    }
                }
            } else {
                return window.cmsFactory.constructors.filter(c => !c.typeDef.hidden && (!c.typeDef.cmsItem || this.cmsPage.itemType));
            }
        },
        categories() {
            let types = {
                preset: {
                    label: `Template|Gabarit`,
                    types: [],
                },
                base: {
                    label: `Base|Base`,
                    types: [],
                },
                layout: {
                    label: `Layout|Disposition`,
                    types: [],
                },
                widget: {
                    label: `Widgets|Widgets`,
                    types: [],
                }
            };
            for (let typeClass of this.typesFiltered) {
                types[typeClass.typeDef.category].types.push(typeClass);
            }

            return types;
        },
        savedTemplates() {
            return this.templates.map(template => ({
                typeDef: {
                    name: template.name,
                    glyph: `fas fa-save`
                },
                templateData: template,
                isTemplate: true
            }));
        }
    },
    methods: {
        selectPreset(preset) {
            if (preset.isTemplate) {
                this.selectTemplate(preset.templateData);
                console.log(`selecting template`, preset.templateData)
            } else {
                this.$emit(`select-block`, preset);
            }
        },
        selectTemplate(template) {
            this.$emit(`select-template`, template);
        },
        deleteTemplate(template) {
            this.$emit(`delete-template`, template.id);
            this.previewTemplateModalVisible = false;
        },
        insertTemplate(template) {
            this.selectTemplate(template);
        },
        editTemplateBlock(template) {
            if (!template) {
                return;
            }
            this.previewTemplateModalVisible = false;
            this.$emit(`edit-template-block`, template);
        },
        openTemplatePreview(template) {
            this.previewTemplate = template;
            this.previewTemplateModalVisible = true;
        },
        openManageTemplates() {
            this.$emit(`manage-templates`);
        }
    }
}
</script>

<style lang="scss">
.properties-select-block-body {
    padding-bottom: 80px;
}

.properties-select-block-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    background: white;
    border-top: 1px solid var(--border-color);
    z-index: 10;
}

.template-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-top: 1rem;
}

.template-item {
    cursor: pointer;
    transition: all 0.3s ease;
    border-radius: 8px;

    &:hover {
        transform: translateY(-4px);
    }

    &:hover .template-block-card__preview {
        outline-color: var(--color-primary);
    }
}

.template-description {
    line-height: 1.4;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
}

.template-large-preview {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    min-width: 100%;
    min-height: 500px;
    overflow: hidden;
    border: 1px solid var(--border-color);
    border-radius: 4px;
    background: white;
    margin-bottom: 1rem;
}

.template-description {
    color: var(--text-muted);
    line-height: 1.5;
    margin-bottom: 1rem;
    font-size: 0.9rem;
}
</style>
